import type * as Phaser from "phaser"
import type { GameData, PowerMode } from "../types"
import type { BossSystem } from "./boss-system"
import type { CombatBallSystem } from "./combat-ball-system"
import type { WaveTimeline } from "./wave-state"

interface HudSyncOptions {
  registry: Phaser.Data.DataManager
  getGameData: () => GameData
  getWaveTimeline: () => WaveTimeline
  getCombatBallSystem: () => CombatBallSystem
  getProjectileGroup: () => Phaser.Physics.Arcade.Group
  getBossSystem: () => BossSystem
  getPowerDuration: (mode: Exclude<PowerMode, null>) => number
}

export class HudSync {
  private readonly options: HudSyncOptions
  private readonly cache = new Map<string, unknown>()

  public constructor(options: HudSyncOptions) {
    this.options = options
  }

  public reset(): void {
    this.cache.clear()
  }

  public setBallPresentation(ballColor: number): void {
    this.setValue("ballColor", ballColor)
  }

  public setPowerMode(mode: PowerMode): void {
    this.setValue("powerMode", mode ?? "")
    this.setValue("powerTimerPct", this.getPowerTimerPct(mode))
  }

  public setHeroMovementState(state: string): void {
    this.setValue("heroMovementState", state)
  }

  public sync(): void {
    const gameData = this.options.getGameData()
    const waveTimeline = this.options.getWaveTimeline()
    const currentWave = waveTimeline.getWave()
    const isBossWave = waveTimeline.isBossWave()
    const waveTimer = isBossWave
      ? waveTimeline.getElapsedTime()
      : waveTimeline.getRemainingTime()
    const waveDuration = waveTimeline.getCurrentWaveDuration()

    this.setValue("hp", gameData.player.hp)
    this.setValue("maxHp", gameData.player.maxHp)
    this.setValue("score", gameData.score)
    this.setValue("currentWave", currentWave)
    this.setValue("waveTimer", waveTimer)
    this.setValue("waveDuration", waveDuration)
    this.setValue("runDistance", gameData.runDistance)
    this.setValue("isBossWave", isBossWave)
    this.setValue("coins", gameData.coins)
    this.setValue("level", gameData.player.level)
    this.setValue("exp", gameData.player.exp)
    this.setValue("expToLevel", gameData.player.expToLevel)
    this.setValue("comboState", gameData.player.combo.state)
    this.setValue("comboHits", gameData.player.combo.hits)
    this.setValue("comboTargetHits", gameData.player.combo.targetHits)
    this.setValue("comboNextEffect", gameData.player.combo.nextEffect)
    this.setValue("comboHitColors", [...gameData.player.combo.hitColors])
    this.setValue("powerMode", gameData.player.powerMode ?? "")
    this.setValue(
      "powerTimerPct",
      this.getPowerTimerPct(gameData.player.powerMode ?? null),
    )
    this.setValue("sectorName", waveTimeline.getCurrentSector().name)
    this.setValue("bossName", waveTimeline.getCurrentSector().boss.name)

    const primarySystem = this.options.getCombatBallSystem()
    if (primarySystem) {
      this.setValue("ballActiveCount", primarySystem.getActiveBallCount())
      this.setValue(
        "projectileActiveCount",
        this.options.getProjectileGroup().countActive(true),
      )
    }

    const hudData = this.options.getBossSystem().collectHudData()
    this.setValue("bossHp", hudData.bossHp)
    this.setValue("bossMaxHp", hudData.bossMaxHp)
    this.setValue("bossPartCount", hudData.bossPartCount)
    this.setValue("bossType", hudData.bossType)
    this.setValue("bossParts", hudData.parts)
    this.setValue("isBossMainBarVisible", hudData.isMainBarVisible)
  }

  private getPowerTimerPct(mode: PowerMode): number {
    if (!mode) return 0
    const duration = this.options.getPowerDuration(mode)
    if (duration <= 0) return 0

    const gameData = this.options.getGameData()
    return Math.max(
      0,
      Math.min(1, (gameData.player.powerTimer ?? 0) / duration),
    )
  }

  private setValue(key: string, value: unknown): void {
    const cached = this.cache.get(key)
    if (
      cached === value ||
      (Array.isArray(cached) &&
        Array.isArray(value) &&
        cached.length === value.length &&
        cached.every((item, index) => item === value[index]))
    ) {
      return
    }

    this.cache.set(key, value)
    this.options.registry.set(key, value)
  }
}
